import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    Input,
    input,
    OnInit,
    output,
    Signal,
    signal,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    TelXLDropdownComponent,
    TelXLLoadingComponent,
    TelXLSearchComponent,
} from '@dxp/elements';
import {
    IAgentDirectoryEvent,
    IAgentState,
    IDirectoryListing,
    IDirectoryListingEvent,
    IDirectorySelection,
    IDropdown,
    IUserDirectory,
    IUserRead,
} from '@dxp/core/models';
import { AgentDirectoryService } from '@dxp/core/services';
import { LIVE_SIGNALR_HUB } from '@dxp/core/signalr';
import { from, fromEvent, merge, of } from 'rxjs';
import {
    catchError,
    debounceTime,
    filter,
    map,
    mergeMap,
    tap,
} from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { calculateUserPresence } from '@dxp/core/helpers';
import { CommonModule } from '@angular/common';
import { startOfDay } from 'date-fns';
import {
    AgentState,
    AvatarVariant,
    AvatarVariants,
    ListingMode,
    MediaOptions,
    MediaType,
} from '@dxp/core/types';
import { TelXLDirectoryListingItemComponent } from '../item/directory-listing-item.component';

const LIMIT = 20;

@Component({
    selector: 'telxl-shared-agent-directory-panel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        TelXLLoadingComponent,
        TelXLDirectoryListingItemComponent,
        TelXLSearchComponent,
        TelXLDropdownComponent,
    ],
    templateUrl: './agent-directory-panel.component.html',
    styleUrl: './agent-directory-panel.component.scss',
})
export class TelXLAgentDirectoryPanelComponent
    implements OnInit, AfterViewInit
{
    readonly #destroyRef = inject(DestroyRef);
    readonly #agentDirectoryService = inject(AgentDirectoryService);
    readonly #liveHub = inject(LIVE_SIGNALR_HUB);

    readonly #requiresEmail = signal<boolean>(false);
    readonly #requiresPhone = signal<boolean>(false);
    readonly #requiresName = signal<boolean>(false);
    readonly #media = signal<MediaOptions>('All');

    @Input()
    set requiresEmail(value: boolean) {
        this.#requiresEmail.set(value);
    }

    @Input()
    set requiresPhone(value: boolean) {
        this.#requiresPhone.set(value);
    }

    @Input()
    set requiresName(value: boolean) {
        this.#requiresName.set(value);
    }

    selected = input<IDirectorySelection>();
    highlight = input<string>();
    agentId = input<string>();
    customButtonTemplate = input<TemplateRef<any>>();
    useOperatorDialing = input<boolean>(false);
    height = input.required<string>();
    displayMediaIcons = input<MediaType[]>([]);
    mode = input<ListingMode[]>([]);
    showMediaFilter = input<boolean>(false);
    restrictions = input<AgentState[]>([]);
    disabled = input<boolean>(false);
    clicked = output<IAgentDirectoryEvent>();

    @ViewChild('scrollContainer', { static: false })
    scrollContainer!: ElementRef;

    readonly #agentDirectory = signal<IUserDirectory>({
        total: 0,
        results: [],
    });

    readonly #activeAgentDirectory: Signal<IAgentState[]>;

    readonly searchText = signal<string>('');
    readonly isLoading = signal<boolean>(false);

    readonly calculateHeight = computed<string>(
        () => `calc(${this.height()} - 100px)`,
    );

    readonly #operatorIdMap = signal<Record<string, string>>({});
    readonly #pendingOperatorIds = new Set<string>();

    mediaMenuItems: IDropdown<MediaOptions>[] = [
        {
            label: 'All',
            value: 'All',
            disabled: false,
        },
        {
            label: 'With Voice',
            value: 'Voice',
            disabled: false,
        },
        {
            label: 'With Email',
            value: 'Email',
            disabled: false,
        },
    ];

    listings = computed<IDirectoryListing[]>(() => {
        const useOperationDialing = this.useOperatorDialing();
        const operatorIds = this.#operatorIdMap();
        const agents = this.#agentDirectory().results;
        const active = this.#activeAgentDirectory();

        return agents.map(agent => {
            const blenderAgent = active.find(a => a.agentId === agent.id);

            const userAccessScope = agent.scopes?.find(
                scope => scope.name === 'bp.access',
            )?.claims;
            const userRole =
                userAccessScope && userAccessScope.length > 0
                    ? userAccessScope[0].value
                    : '';

            const operatorId = useOperationDialing
                ? (operatorIds[agent.id] ?? '')
                : '';

            return {
                id: agent.id,
                name: agent.name,
                phoneNumber: useOperationDialing
                    ? operatorId
                    : agent.phoneNumber,
                email: agent.emailAddress,
                presence: blenderAgent
                    ? calculateUserPresence(
                          blenderAgent.state,
                          blenderAgent.stateSince,
                          blenderAgent.breakName,
                      )
                    : calculateUserPresence(
                          'LoggedOut',
                          startOfDay(new Date()),
                      ),
                userRole: this.getMappedUserRole(userRole),
                object: JSON.stringify(agent),
            };
        });
    });

    getMappedUserRole(role: string): AvatarVariant {
        const mappedUserRole: Record<string, AvatarVariant> = {
            agent: AvatarVariants.Agents,
            supervisor: AvatarVariants.Supervisor,
        };
        const propertyExists = Object.prototype.hasOwnProperty.call(
            mappedUserRole,
            role,
        );

        return propertyExists ? mappedUserRole[role] : 'agents';
    }

    constructor() {
        const initialFetch$ = this.#agentDirectoryService
            .fetchActiveAgents(
                undefined,
                undefined,
                this.#requiresName(),
                this.#requiresEmail(),
                this.#requiresPhone(),
            )
            .pipe(
                map(agents =>
                    agents.map(a => ({
                        agentId: a.id,
                        agentName: a.name,
                        state: a.status,
                        stateSince: a.statusChangedAt,
                        breakName: a.breakName,
                        businessUnitIds: [],
                    }) as IAgentState),
                ),
            );

        this.#activeAgentDirectory = toSignal(
            merge(initialFetch$, this.#liveHub.agents$),
            { initialValue: [] },
        );
    }

    ngOnInit() {
        this.#loadAgents(true);
    }

    ngAfterViewInit() {
        if (this.scrollContainer) {
            fromEvent(this.scrollContainer.nativeElement, 'scroll')
                .pipe(
                    debounceTime(300),
                    map(() => this.scrollContainer.nativeElement),
                    filter(
                        container =>
                            container.scrollTop + container.clientHeight >=
                            container.scrollHeight - 50,
                    ),
                    tap(() => this.#loadAgents(false)),
                    takeUntilDestroyed(this.#destroyRef),
                )
                .subscribe();
        }
    }

    selectedChanged(event: IDirectoryListingEvent) {
        this.clicked.emit({
            type: event.type,
            item: {
                ...(this.#agentDirectory().results.find(
                    a => a.id === event.item.id,
                ) as IUserRead),
                phoneNumber: this.useOperatorDialing()
                    ? this.#operatorIdMap()[event.item.id]
                    : event.item.phoneNumber,
            },
        });
    }

    refresh(agent: IUserRead) {
        this.#agentDirectory.update(current => {
            const index = current.results.findIndex(c => c.id === agent.id);

            if (index !== -1) {
                const updatedDirectory = [...current.results];
                updatedDirectory[index] = agent;

                return {
                    total: current.total,
                    results: updatedDirectory,
                };
            } else {
                return current;
            }
        });

        if (this.useOperatorDialing()) {
            this.#ensureOperatorIdsFor([agent]);
        }
    }

    #hasMoreData(): boolean {
        return (
            this.#agentDirectory().results.length < this.#agentDirectory().total
        );
    }

    #loadAgents(initialLoad: boolean) {
        if ((!initialLoad && !this.#hasMoreData()) || this.isLoading()) return;

        this.isLoading.set(true);

        this.#agentDirectoryService
            .fetchAgentDirectory(
                LIMIT,
                this.#agentDirectory().results.length,
                this.searchText().length > 0 ? this.searchText() : undefined,
                this.#requiresName(),
                this.#requiresEmail(),
                this.#requiresPhone(),
                this.agentId(),
            )
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(data => {
                this.#agentDirectory.update(current => {
                    return {
                        total: data.total,
                        results: [...current.results, ...data.results],
                    };
                });

                if (this.useOperatorDialing()) {
                    this.#ensureOperatorIdsFor(data.results);
                }

                this.isLoading.set(false);
            });
    }

    searchChanged(searchText: string) {
        this.#agentDirectory.set({
            total: 0,
            results: [],
        });

        this.searchText.set(searchText);
        this.#loadAgents(true);
    }

    restrictMode(listing: IDirectoryListing): ListingMode[] {
        if (
            this.restrictions().length === 0 ||
            this.restrictions().includes(listing.presence?.state ?? 'LoggedOut')
        ) {
            return this.mode();
        }

        return this.mode().filter(m => m !== 'emit');
    }

    get media(): MediaOptions {
        return this.#media();
    }

    set media(value: string) {
        this.#media.set(value as MediaOptions);

        switch (this.#media()) {
            case 'All': {
                this.#requiresEmail.set(false);
                this.#requiresPhone.set(false);
                break;
            }
            case 'Voice': {
                this.#requiresEmail.set(false);
                this.#requiresPhone.set(true);
                break;
            }
            case 'Email': {
                this.#requiresEmail.set(true);
                this.#requiresPhone.set(false);
                break;
            }
        }

        this.#agentDirectory.set({
            total: 0,
            results: [],
        });

        this.#loadAgents(true);
    }

    #ensureOperatorIdsFor(agents: IUserRead[]) {
        const cache = this.#operatorIdMap();
        const toFetch = agents
            .map(a => a.id)
            .filter(id => !cache[id] && !this.#pendingOperatorIds.has(id));

        if (toFetch.length === 0) return;

        toFetch.forEach(id => this.#pendingOperatorIds.add(id));

        from(toFetch)
            .pipe(
                mergeMap(
                    id =>
                        this.#agentDirectoryService
                            .fetchAgentOperatorId(id)
                            .pipe(
                                map(result =>
                                    result?.isSuccess &&
                                    result?.data &&
                                    result?.data.length > 0
                                        ? {
                                              id,
                                              operatorId:
                                                  result.data[0].ID.toString(),
                                          }
                                        : { id, operatorId: '' },
                                ),
                                catchError(() => of({ id, operatorId: '' })),
                                tap(({ id }) =>
                                    this.#pendingOperatorIds.delete(id),
                                ),
                            ),
                    6,
                ),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe(({ id, operatorId }) => {
                this.#operatorIdMap.update(curr => ({
                    ...curr,
                    [id]: operatorId,
                }));
            });
    }
}
